import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Autocomplete, Box, Button, Grid, Stack, TextField } from '@mui/material'
import Header from 'components/Header'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useBasePath, useSession } from 'components/Providers'
import { saveAssetDraft, deleteAssetDraft } from 'store/asset/need-attention'
import { Controller, useForm } from 'react-hook-form'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import _ from 'lodash'

export default function AcceptDraftForm({ asset, assetType }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const basePath = useBasePath()
  const { district } = useSession()

  const schema = yup.object().shape({
    assetNumber: yup.string().nullable().transform(v => v ?? '').required(t('Required')),
    serialNumber: yup.string().nullable().transform(v => v ?? '').required(t('Required')),
  })

  const { control, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...asset,
      purchaseDate: asset?.purchaseDate ? dayjs(asset.purchaseDate) : dayjs(),
    },
  })

  const onSubmit = async data => {
    const res = await dispatch(saveAssetDraft({ id: asset.id, ..._.omit(data, ['assignInfo', 'campus', 'conditionId']) }))
    if (res.payload) {
      navigate(basePath)
    }
  }

  const handleDelete = async () => {
    const res = await dispatch(deleteAssetDraft({ ids: [asset.id] }))
    if (res.payload) navigate(basePath)
  }

  return (
    <Box>
      <Header title={t('Update Device')} small close="." borderBottom />
      <Box component="form" onSubmit={handleSubmit(onSubmit)} py={2} px={1}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Controller
              name="typeId"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  value={assetType.find(option => option.code === field.value) ?? null}
                  options={assetType}
                  getOptionLabel={option => option.name || ''}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('Type')}
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={!!formState.errors.typeId}
                      helperText={formState.errors.typeId?.message}
                    />
                  )}
                  onChange={(_, newValue) => field.onChange(newValue ? newValue.code : null)}
                  noOptionsText={t('No options')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="brand"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Brand')}
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="model"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Model')}
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="assetNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Asset Number')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!formState.errors.assetNumber}
                  helperText={formState.errors.assetNumber?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="serialNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Serial Number')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!formState.errors.serialNumber}
                  helperText={formState.errors.serialNumber?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="purchaseDate"
              control={control}
              render={({ field: { value, ...field } }) => (
                <DatePicker
                  sx={{ width: '100%' }}
                  slotProps={{ textField: { size: 'small' } }}
                  label={t('Purchase Date')}
                  value={dayjs(value) || null}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Notes')}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
        <Stack direction={'row'} spacing={1} mt={2} justifyContent={'flex-end'}>
          {district?.isServiceProvider && (
            <Button onClick={handleDelete} size="small" variant="contained" color="error">
              {t('Delete')}
            </Button>
          )}
          <Button type="submit" size="small" variant="contained" color="primary">
            {t('Save')}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}
